// Life & Wealth Solutions — AI Agent Control Panel (Fixed V34)
// Full 13-agent management with real activate/deactivate/configure

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type AgentStatus = 'active' | 'inactive' | 'initializing' | 'error';

interface Agent {
  id: number;
  name: string;
  title: string;
  description: string;
  category: 'magnificent_seven' | 'platform' | 'infrastructure';
  status: AgentStatus;
  configPath: string;
  metrics: { label: string; value: string }[];
}

const INITIAL_AGENTS: Agent[] = [
  { id: 1, name: 'Sarah', title: 'Lead Intake & Qualification', category: 'magnificent_seven', status: 'active', configPath: '/agent-portal', description: 'Handles inbound lead intake, initial qualification, and routing to appropriate pipeline stages.', metrics: [{ label: 'Leads Today', value: '14' }, { label: 'Qualified', value: '9' }] },
  { id: 2, name: 'Mike', title: 'Outbound Prospecting', category: 'magnificent_seven', status: 'active', configPath: '/agent-portal', description: 'Executes outbound prospecting campaigns, cold outreach, and appointment setting.', metrics: [{ label: 'Calls Today', value: '47' }, { label: 'Appointments', value: '3' }] },
  { id: 3, name: 'Emma', title: 'Quote Generation', category: 'magnificent_seven', status: 'active', configPath: '/agent-portal', description: 'Generates real-time insurance quotes across all carrier lines and presents to clients.', metrics: [{ label: 'Quotes Today', value: '22' }, { label: 'Presented', value: '18' }] },
  { id: 4, name: 'Tom', title: 'Objection Handling', category: 'magnificent_seven', status: 'inactive', configPath: '/agent-portal', description: 'Handles client objections, negotiation responses, and price sensitivity conversations.', metrics: [{ label: 'Objections', value: '0' }, { label: 'Resolved', value: '0' }] },
  { id: 5, name: 'Jessica', title: 'Follow-Up & Nurture', category: 'magnificent_seven', status: 'active', configPath: '/agent-portal', description: 'Manages follow-up sequences, lead nurturing drips, and re-engagement campaigns.', metrics: [{ label: 'Sequences', value: '31' }, { label: 'Replies', value: '7' }] },
  { id: 6, name: 'Adam', title: 'Cross-Sell & Upsell', category: 'magnificent_seven', status: 'active', configPath: '/agent-portal', description: 'Identifies and executes cross-sell and upsell opportunities within existing client base.', metrics: [{ label: 'Identified', value: '6' }, { label: 'Converted', value: '2' }] },
  { id: 7, name: 'Charlotte', title: 'Client Retention', category: 'magnificent_seven', status: 'active', configPath: '/agent-portal', description: 'Manages client retention, renewal outreach, and satisfaction monitoring.', metrics: [{ label: 'At Risk', value: '3' }, { label: 'Retained', value: '1' }] },
  { id: 8, name: 'My Phoenix', title: 'PLG Navigator — Retention Engine', category: 'platform', status: 'active', configPath: '/agent-portal', description: 'Drives product-led growth, manages onboarding drips, and prevents client churn proactively.', metrics: [{ label: 'Drip Active', value: '48' }, { label: 'Churn Risk', value: '2' }] },
  { id: 9, name: 'My Maestro', title: 'Orchestrator — Chief of Staff', category: 'platform', status: 'active', configPath: '/predictions', description: 'Coordinates all agent activities, detects whale opportunities, and manages escalation routing.', metrics: [{ label: 'Whales', value: '2' }, { label: 'Escalations', value: '1' }] },
  { id: 10, name: 'My Mini-Me', title: 'Digital Proxy', category: 'platform', status: 'active', configPath: '/agent-portal', description: 'Acts as your digital twin for routine decisions, learning from your approval and rejection patterns.', metrics: [{ label: 'Auto-Handled', value: '19' }, { label: 'Escalated', value: '3' }] },
  { id: 11, name: 'My Sentinel', title: 'The General — 50-State Compliance', category: 'platform', status: 'active', configPath: '/compliance', description: 'Monitors all policies against 50-state regulations, generates Audit Shield™ certificates, and prevents compliance violations.', metrics: [{ label: 'Checks Today', value: '847' }, { label: 'Violations Stopped', value: '4' }] },
  { id: 12, name: 'Agent 12 (You)', title: 'Human Licensed Authority — HITL', category: 'platform', status: 'active', configPath: '/agent12', description: 'You are Agent 12. Your licensed authority provides the final binding signature on all high-stakes decisions.', metrics: [{ label: 'Pending', value: '3' }, { label: 'Approved Today', value: '7' }] },
  { id: 13, name: 'Autonomic Watchdog', title: 'Self-Healing Infrastructure', category: 'infrastructure', status: 'active', configPath: '/agent-portal', description: 'Monitors the entire platform 24/7 and automatically heals failures before they affect operations.', metrics: [{ label: 'Uptime', value: '99.9%' }, { label: 'Auto-Healed', value: '2' }] },
];

const statusColors: Record<AgentStatus, string> = {
  active: 'bg-green-100 text-green-700 border border-green-200',
  inactive: 'bg-gray-100 text-gray-500 border border-gray-200',
  initializing: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
  error: 'bg-red-100 text-red-700 border border-red-200',
};

const statusDot: Record<AgentStatus, string> = {
  active: 'bg-green-500',
  inactive: 'bg-gray-400',
  initializing: 'bg-yellow-500 animate-pulse',
  error: 'bg-red-500',
};

const categoryLabel: Record<string, string> = {
  magnificent_seven: 'My Magnificent Seven',
  platform: 'Platform Agents',
  infrastructure: 'Infrastructure',
};

const AIAgentControl: React.FC = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>(INITIAL_AGENTS);
  const [activatingId, setActivatingId] = useState<number | null>(null);

  const toggleAgent = async (id: number) => {
    // Agent 12 and Autonomic Watchdog cannot be deactivated
    if (id === 12 || id === 13) return;
    setActivatingId(id);
    // Simulate brief init delay
    await new Promise(r => setTimeout(r, 800));
    setAgents(prev =>
      prev.map(a =>
        a.id === id
          ? { ...a, status: a.status === 'active' ? 'inactive' : 'active' }
          : a
      )
    );
    setActivatingId(null);
  };

  const activateAll = async () => {
    const inactive = agents.filter(a => a.status === 'inactive');
    for (const agent of inactive) {
      setActivatingId(agent.id);
      await new Promise(r => setTimeout(r, 300));
      setAgents(prev => prev.map(a => a.id === agent.id ? { ...a, status: 'active' } : a));
    }
    setActivatingId(null);
  };

  const activeCount = agents.filter(a => a.status === 'active').length;

  const grouped = agents.reduce((acc, agent) => {
    if (!acc[agent.category]) acc[agent.category] = [];
    acc[agent.category].push(agent);
    return acc;
  }, {} as Record<string, Agent[]>);

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Agent Control Panel</h1>
          <p className="text-sm text-gray-500 mt-1">
            {activeCount} of {agents.length} agents active — Mynd My Business in a Box™
          </p>
        </div>
        <div className="flex items-center gap-3">
          {activeCount < agents.length && (
            <button
              onClick={activateAll}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <img src="/phoenix-rescue-logo.png" alt="" className="w-5 h-5 object-contain" />
              Activate All Agents
            </button>
          )}
          <button
            onClick={() => navigate('/agent-portal')}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Open Agent Portal →
          </button>
        </div>
      </div>

      {/* Status bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Active Agents', value: activeCount, color: 'text-green-600' },
          { label: 'Inactive', value: agents.filter(a => a.status === 'inactive').length, color: 'text-gray-500' },
          { label: 'Policies Today', value: '22', color: 'text-blue-600' },
          { label: 'Revenue Today', value: '$8,450', color: 'text-purple-600' },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 font-medium">{stat.label}</p>
            <p className={`text-2xl font-bold mt-1 ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Agent Groups */}
      {['magnificent_seven', 'platform', 'infrastructure'].map(category => (
        <div key={category}>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
            {categoryLabel[category]}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {grouped[category]?.map(agent => (
              <div
                key={agent.id}
                className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3 hover:shadow-md transition-shadow"
              >
                {/* Agent header */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[activatingId === agent.id ? 'initializing' : agent.status]}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusDot[activatingId === agent.id ? 'initializing' : agent.status]}`} />
                        {activatingId === agent.id ? 'Starting...' : agent.status.charAt(0).toUpperCase() + agent.status.slice(1)}
                      </span>
                      <span className="text-xs text-gray-400">#{agent.id}</span>
                    </div>
                    <h3 className="text-sm font-bold text-gray-900 mt-1">{agent.name}</h3>
                    <p className="text-xs text-gray-500 truncate">{agent.title}</p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-xs text-gray-600 line-clamp-2">{agent.description}</p>

                {/* Metrics */}
                <div className="grid grid-cols-2 gap-2">
                  {agent.metrics.map(m => (
                    <div key={m.label} className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-sm font-bold text-gray-900">{m.value}</p>
                      <p className="text-xs text-gray-500">{m.label}</p>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-auto pt-1">
                  <button
                    onClick={() => navigate(agent.configPath)}
                    className="flex-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    Configure
                  </button>
                  {agent.id !== 12 && agent.id !== 13 ? (
                    <button
                      onClick={() => toggleAgent(agent.id)}
                      disabled={activatingId === agent.id}
                      className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                        agent.status === 'active'
                          ? 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                          : 'text-green-700 bg-green-50 hover:bg-green-100'
                      }`}
                    >
                      {activatingId === agent.id ? '...' : agent.status === 'active' ? 'Deactivate' : 'Activate'}
                    </button>
                  ) : (
                    <div className="flex-1 px-3 py-1.5 text-xs text-center text-gray-400 bg-gray-50 rounded-lg">
                      {agent.id === 12 ? 'Always On' : 'Auto'}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AIAgentControl;
